import { JSX } from 'preact';
import { EditorProps, ViewerProps, RichProp } from 'client/courses/components/page-builder';
import { Color, autoColor } from 'shared/colors';
import { CTAState } from 'client/courses/components/page-builder/cta-button';

export function extractReadonlyProps<T>(props: EditorProps<T> | ViewerProps<T>) {
  const editProps = props as EditorProps<T>;
  const readonly = !editProps.setState;
  return { editProps, readonly };
}

export function extractBlockState(props: any) {
  return 'blockState' in props ? props.blockState : undefined;
}

export function createGenerateState<T>(initialState: T) {
  return function generateState(opts: Partial<T>): T {
    return { ...initialState, ...opts };
  };
}

/**
 * Enhanced generateState for blocks with CTAs that automatically sets appropriate CTA colors
 */
export function createGenerateStateWithSmartCTA<T extends { callToAction: CTAState }>(
  initialState: T,
  getBlockBgColor?: (opts: Partial<T>) => string | undefined,
) {
  return function generateState(opts: Partial<T>): T {
    const baseState = { ...initialState, ...opts };

    // Determine the block's background color
    const blockBgColor = getBlockBgColor ? getBlockBgColor(opts) : undefined;

    // Get contrasting CTA colors if needed
    const ctaColorOverrides = blockBgColor
      ? {
          bgcolor: Color.white,
          fgcolor: autoColor(blockBgColor),
        }
      : {};

    // Merge the CTA color overrides
    return {
      ...baseState,
      callToAction: {
        ...baseState.callToAction,
        ...ctaColorOverrides,
      },
    };
  };
}

type SectionHeaderProps<T> = {
  state: T;
  setState?: (state: T) => void;
  path?: string;
};

export function SectionHeader<T>({
  state,
  setState,
  path = 'headerRichText',
}: SectionHeaderProps<T>): JSX.Element {
  return (
    <div class="text-center @lg:mb-16 @sm:mb-12 mb-8">
      <RichProp
        state={state}
        setState={setState}
        path={path}
        class="font-bold @lg:text-4xl @md:text-3xl text-2xl text-gray-900 mb-3"
        toolbarPosition="center"
      />
    </div>
  );
}
